import React from 'react'
import { Button } from '@/components/ui/button'
import { PaymentMethodDto } from 'api/PaymentProvider/Types/PaymentMethodDto'
import { WalletCurrencyDto } from 'api/Wallet/Types/WalletCurrencyDto'
import {
  FormBaseButtonContainer,
  FormBaseContainer,
  FormBaseErrorMessage,
  FormBaseFieldBlock,
  FormBaseFieldGroup,
  FormBaseFieldLabel,
  FormBaseRow,
} from 'components/Form/Form.styles'
import { Field, Form, FormikErrors, FormikProps, withFormik } from 'formik'
import { FormSubmitValues, TwoDecimalPlacesPattern } from 'utils/formValidation'

export interface AddWithdrawalThresholdFormValues {
  paymentProviderMethodId: string
  currencyId: string
  bankCode: string
  amount: number
}

const AddWithdrawalThresholdFormUI: React.FC<
  FormikProps<AddWithdrawalThresholdFormValues> & OuterProps
> = (props) => {
  const { handleSubmit, setSubmitting, isSubmitting, errors } = props

  const handleSubmitForm = () => {
    setSubmitting(true)
    handleSubmit()
  }

  return (
    <FormBaseContainer>
      <Form>
        <FormBaseFieldBlock>
          <FormBaseRow>
            <FormBaseFieldLabel>Payment Provider</FormBaseFieldLabel>
            <FormBaseFieldGroup>
              <Field component='select' name='paymentProviderMethodId'>
                <option value='' disabled>
                  None
                </option>
                {(props.paymentProviders || []).map((paymentProvider) => (
                  <option key={paymentProvider.id} value={paymentProvider.id}>
                    {paymentProvider.name}
                  </option>
                ))}
              </Field>
            </FormBaseFieldGroup>
          </FormBaseRow>
          {errors.paymentProviderMethodId && (
            <FormBaseErrorMessage>{errors.paymentProviderMethodId}</FormBaseErrorMessage>
          )}
        </FormBaseFieldBlock>
        <FormBaseFieldBlock>
          <FormBaseRow>
            <FormBaseFieldLabel>Currency</FormBaseFieldLabel>
            <FormBaseFieldGroup>
              <Field component='select' name='currencyId'>
                <option value='' disabled>
                  None
                </option>
                {(props.currencies || []).map((currency) => (
                  <option key={currency.id} value={currency.id}>
                    {currency.id}
                  </option>
                ))}
              </Field>
            </FormBaseFieldGroup>
          </FormBaseRow>
          {errors.currencyId && <FormBaseErrorMessage>{errors.currencyId}</FormBaseErrorMessage>}
        </FormBaseFieldBlock>
        <FormBaseFieldBlock>
          <FormBaseRow>
            <FormBaseFieldLabel>Bank Code</FormBaseFieldLabel>
            <FormBaseFieldGroup>
              <Field type='text' name='bankCode' />
            </FormBaseFieldGroup>
          </FormBaseRow>
        </FormBaseFieldBlock>
        <FormBaseFieldBlock>
          <FormBaseRow>
            <FormBaseFieldLabel>Amount</FormBaseFieldLabel>
            <FormBaseFieldGroup>
              <Field type='number' name='amount' min='0' step='0.01' />
            </FormBaseFieldGroup>
          </FormBaseRow>
          {errors.amount && <FormBaseErrorMessage>{errors.amount}</FormBaseErrorMessage>}
        </FormBaseFieldBlock>
        <FormBaseButtonContainer>
          <Button disabled={isSubmitting} key='button' onClick={handleSubmitForm} type='button'>
            Add
          </Button>
        </FormBaseButtonContainer>
      </Form>
    </FormBaseContainer>
  )
}

interface OuterProps {
  paymentProviders: PaymentMethodDto[]
  currencies: WalletCurrencyDto[]

  onSubmit(values: FormSubmitValues<AddWithdrawalThresholdFormValues>): Promise<void>
}

export const validateAmount = (amount: number | undefined): string | undefined => {
  if (!amount) {
    return 'required'
  }
  if (amount < 0) {
    return 'Amount should be greater than 0'
  }
  if (!TwoDecimalPlacesPattern.test(String(amount))) {
    return 'Amount can have a maximum of 2 decimal places'
  }
  return undefined
}

export const AddWithdrawalThresholdForm = withFormik<OuterProps, AddWithdrawalThresholdFormValues>({
  mapPropsToValues: () => {
    return {
      paymentProviderMethodId: '',
      currencyId: '',
      bankCode: '',
      amount: 0,
    }
  },
  handleSubmit: async (values, { props, setSubmitting }) => {
    try {
      await props.onSubmit(values)
    } finally {
      setSubmitting(false)
    }
  },
  validate: (values) => {
    const errors: FormikErrors<AddWithdrawalThresholdFormValues> = {}
    if (!values.paymentProviderMethodId) {
      errors.paymentProviderMethodId = 'required'
    }
    if (!values.currencyId) {
      errors.currencyId = 'required'
    }
    const amountError = validateAmount(values.amount)
    if (amountError) {
      errors.amount = amountError
    }

    return errors
  },
  enableReinitialize: true,
})(AddWithdrawalThresholdFormUI)
